/***
 *
 *   TICKET CONTROLS / TOOLBAR
 *   Combines the reference TicketToolbar + TicketFiltersBar. Sort and filters
 *   sit inline on sm+; the modal path stays as the <sm fallback (reference's
 *   mobile filter drawer, implemented with the existing ViewContext modal).
 *
 *   Search-param map (unchanged data layer):
 *     q → search, priority, assignee, creator, me, c → category,
 *     by / order → sort, view → kanban|list
 *
 **********/

import React, { useState, useContext, useEffect, useMemo } from 'react';
import {
    Button,
    ViewContext,
    TicketForm,
    Search,
    Icon,
    cn,
} from 'components/lib';
import Style from './controls.tailwind';
import { useTranslate } from 'app/translations';
import { useLiveQuery } from 'components/hooks/websocket';
import { useProjectContext } from 'components/lib';
import { AuthContext } from 'app/auth';
import { ViewToggle } from './viewToggle';
import { CategoryMultiSelect } from './categoryMultiSelect';
import { PriorityBadge } from './priorityBadge';

const SORT_OPTIONS = [
    { value: 'date_created:desc', by: 'date_created', order: 'desc', labelKey: 'ticket.filters.newest' },
    { value: 'date_created:asc', by: 'date_created', order: 'asc', labelKey: 'ticket.filters.oldest' },
    { value: 'priority:desc', by: 'priority', order: 'desc', labelKey: 'ticket.filters.prioritySort' },
    { value: 'due_date:asc', by: 'due_date', order: 'asc', labelKey: 'ticket.filters.dueDate' },
];

export const TicketControls = ({
    setSearchParams,
    searchParams,
    getSearchParams,
    viewMode,
    onViewModeChange,
    allTickets,
}) => {
    const { _t } = useTranslate();
    const viewContext = useContext(ViewContext);
    const [searchValue, setSearchValue] = useState(searchParams.get('q') || '');
    const [searchKey, setSearchKey] = useState(0);
    const {
        user: { permission },
    } = useContext(AuthContext);

    const { projectUsers, selectedProject, isAllProjectSelected, projectUser } = useProjectContext();
    const categories = useLiveQuery(
        'ticket_categories',
        { project_id: selectedProject?.id },
        { enabled: !!selectedProject?.id },
    );

    const currCategory = categories.data?.find((category) => {
        return category.id === searchParams.get('c');
    });

    const updateFilterParams = (params) => {
        let newParams = getSearchParams();
        newParams = { ...newParams, ...params };

        Object.keys(newParams).forEach((key) => {
            if (!newParams[key]) delete newParams[key];
        });

        setSearchParams(newParams);
    };

    const clearFilter = (key) => {
        const next = getSearchParams();
        delete next[key];
        if (key === 'me') delete next.me;
        setSearchParams(next);
    };

    const resetFilters = () => {
        const next = getSearchParams();
        ['priority', 'assignee', 'creator', 'me', 'q', 'by', 'order'].forEach((k) => delete next[k]);
        setSearchParams(next);
        setSearchValue('');
        setSearchKey((k) => k + 1);
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const current = searchParams.get('q') || '';
            if (searchValue === current) return;
            updateFilterParams({ q: searchValue || undefined });
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchValue]);

    // Keep local search in sync when params are cleared elsewhere.
    useEffect(() => {
        const q = searchParams.get('q') || '';
        if (q !== searchValue && !searchValue) setSearchValue(q);
        if (!q && searchValue && !searchParams.has('q')) setSearchValue('');
    }, [searchParams]);

    const categoryAssignees = currCategory?.integration_data?.users || [];

    const categoryIds = useMemo(() => {
        const raw = searchParams.get('c');
        if (!raw || raw === 'all') return [];
        return raw.split(',').filter(Boolean);
    }, [searchParams]);

    const sortValue = (() => {
        const by = searchParams.get('by') || 'date_created';
        const order = searchParams.get('order') || 'desc';
        const match = SORT_OPTIONS.find((o) => o.by === by && o.order === order);
        return match?.value || 'date_created:desc';
    })();

    const activeChips = [];
    if (searchParams.get('priority')) {
        activeChips.push({
            key: 'priority',
            node: <PriorityBadge priority={searchParams.get('priority')} />,
            clear: () => clearFilter('priority'),
        });
    }
    if (searchParams.get('assignee')) {
        activeChips.push({
            key: 'assignee',
            node: (
                <span className="flex items-center gap-1.5">
                    <Icon image="user-check" size={14} color="currentColor" className="text-text-muted" />
                    <span className="max-w-[12rem] truncate">{searchParams.get('assignee')}</span>
                </span>
            ),
            clear: () => clearFilter('assignee'),
        });
    }
    if (searchParams.get('creator')) {
        activeChips.push({
            key: 'creator',
            node: (
                <span className="flex items-center gap-1.5">
                    <Icon image="edit-2" size={14} color="currentColor" className="text-text-muted" />
                    <span className="max-w-[12rem] truncate">{searchParams.get('creator')}</span>
                </span>
            ),
            clear: () => clearFilter('creator'),
        });
    }
    if (searchParams.get('me')) {
        activeChips.push({
            key: 'me',
            node: <span>{_t('ticket.creator.me')}</span>,
            clear: () => clearFilter('me'),
        });
    }

    const filterParamsLength = ['priority', 'assignee', 'me', 'creator'].filter((k) =>
        searchParams.get(k),
    ).length;

    const openFiltersModal = () => {
        viewContext.modal.show(
            {
                title: _t('ticket.filter.title'),
                form: {
                    priority: {
                        type: 'select',
                        label: _t('priority.label'),
                        options: [
                            { label: _t('none'), value: '' },
                            { label: _t('priority.low'), value: 'low' },
                            { label: _t('priority.medium'), value: 'medium' },
                            { label: _t('priority.high'), value: 'high' },
                            { label: _t('priority.urgent'), value: 'urgent' },
                        ],
                        default: searchParams.get('priority'),
                    },
                    ...(permission !== 'contact' &&
                        !isAllProjectSelected && {
                            assignee: {
                                type: 'select',
                                label: _t('assignee.one'),
                                options: [
                                    { label: _t('none'), value: '' },
                                    ...categoryAssignees.map((user) => ({
                                        label: user.name,
                                        value: user.email,
                                    })),
                                ],
                                default: searchParams.get('assignee'),
                            },
                        }),
                    ...(projectUser?.role !== 'contact' && {
                        me: {
                            type: 'checkbox',
                            options: ['yes'],
                            label: _t('ticket.creator.me'),
                            default: [searchParams.get('me')],
                        },
                    }),
                    ...(projectUser?.role !== 'contact' && {
                        creator: {
                            type: 'select',
                            label: _t('ticket.creator.one'),
                            options: [
                                { label: _t('none'), value: '' },
                                ...projectUsers.map((user) => ({
                                    label:
                                        user.first_name +
                                        ' ' +
                                        user.last_name +
                                        ' (' +
                                        user.email +
                                        ')',
                                    value: user.email,
                                })),
                            ],
                            default: searchParams.get('creator'),
                        },
                    }),
                },
                buttonText: _t('save'),
            },
            (data) => {
                if (data.me && data.me[0] === 'null') data.me = undefined;
                data.me = data.me?.filter((val) => val !== null);
                updateFilterParams(data);
            },
        );
    };

    const chipBtn =
        'inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2 py-1 text-xs font-medium text-text-primary transition-colors hover:bg-muted/70';

    return (
        <section className={Style.container}>
            <div className={Style.toolbar}>
                <div className={Style.toggleSlot}>
                    <ViewToggle viewMode={viewMode} onChange={onViewModeChange} />
                </div>

                {selectedProject && (
                    <Button
                        text={_t('ticket.create.title')}
                        variant="accent"
                        size="sm"
                        icon="plus"
                        iconColor="currentColor"
                        className={Style.createBtn}
                        ariaLabel={_t('ticket.create.title')}
                        action={() => {
            viewContext.modal.show({
                title: _t('ticket.create.title'),
                width: '48rem',
                component: (
                    <TicketForm
                        cancel
                        categories={categories.data}
                        category={searchParams.get('c')}
                    />
                ),
            });
                        }}
                    />
                )}

                <div className={Style.filtersSlot}>
                    <div className={Style.filtersRow}>
                        <Search
                            key={searchKey}
                            placeholder={_t('ticket.filters.search')}
                            value={searchValue}
                            callback={(v) => setSearchValue(v)}
                            className={Style.search}
                        />

                        {!isAllProjectSelected && (
                            <div className="hidden sm:block">
                                <CategoryMultiSelect
                                    categories={categories}
                                    tickets={allTickets}
                                    selected={categoryIds}
                                    projectId={selectedProject?.id}
                                    onChange={(ids) => {
                                        if (!ids || ids.length === 0) {
                                            updateFilterParams({ c: 'all' });
                                        } else if (ids.length === 1) {
                                            updateFilterParams({ c: ids[0] });
                                        } else {
                                            updateFilterParams({ c: ids.join(',') });
                                        }
                                    }}
                                />
                            </div>
                        )}

                        <div className="hidden sm:block">
                            <div className="relative">
                                <Icon
                                    image="sliders"
                                    size={14}
                                    color="currentColor"
                                    className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-text-muted"
                                />
                                <select
                                    aria-label={_t('ticket.filters.sortBy')}
                                    className={Style.sortSelect}
                                    value={sortValue}
                                    onChange={(e) => {
                                        const opt = SORT_OPTIONS.find((o) => o.value === e.target.value);
                                        if (!opt) return;
                                        updateFilterParams({ by: opt.by, order: opt.order });
                                    }}>
                                    {SORT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {_t(o.labelKey)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <Button
                            text={_t('filters')}
                            variant="secondary"
                            size="sm"
                            icon="filter"
                            iconColor="currentColor"
                            count={filterParamsLength}
                            className={Style.filterBtn}
                            action={openFiltersModal}
                        />

                        {/* Sort stays reachable on mobile via the legacy modal */}
                        <Button
                            text={_t('sorts')}
                            variant="secondary"
                            size="sm"
                            icon="sliders"
                            iconColor="currentColor"
                            className="sm:hidden"
                            action={() => {
                                viewContext.modal.show(
                                    {
                                        title: _t('ticket.sort.title'),
                                        form: {
                                            by: {
                                                type: 'select',
                                                label: _t('ticket.filter.by.label'),
                                                required: false,
                                                options: [
                                                    {
                                                        label: _t('ticket.filter.by.date_created'),
                                                        value: 'date_created',
                                                    },
                                                    {
                                                        label: _t('ticket.filter.by.due_date'),
                                                        value: 'due_date',
                                                    },
                                                    {
                                                        label: _t('ticket.filter.by.priority'),
                                                        value: 'priority',
                                                    },
                                                ],
                                                default: searchParams.get('by') || 'date_created',
                                            },
                                            order: {
                                                type: 'select',
                                                label: _t('ticket.filter.order.label'),
                                                required: true,
                                                options: [
                                                    { label: _t('ticket.filter.order.asc'), value: 'asc' },
                                                    { label: _t('ticket.filter.order.desc'), value: 'desc' },
                                                ],
                                                default: searchParams.get('order') || 'desc',
                                            },
                                        },
                                        buttonText: _t('save'),
                                    },
                                    (data) => updateFilterParams(data),
                                );
                            }}
                        />
                    </div>
                </div>
            </div>

            {activeChips.length > 0 && (
                <div className={Style.chips}>
                    {activeChips.map((chip) => (
                        <button
                            key={chip.key}
                            type="button"
                            onClick={chip.clear}
                            className={cn(chipBtn, 'pr-1')}>
                            {chip.node}
                            <Icon image="x" size={12} color="currentColor" className="shrink-0 opacity-70" />
                        </button>
                    ))}
                    <Button
                        text={_t('ticket.filters.reset')}
                        variant="ghost"
                        size="sm"
                        icon="rotate-ccw"
                        iconColor="currentColor"
                        className="h-7 gap-1.5 px-2 text-xs"
                        action={resetFilters}
                    />
                </div>
            )}
        </section>
    );
};
